import {
  ReactElement,
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from 'react'
import { fetchMissionCAST, toggleMissionCast } from './MissionCASTSlice'
import { useMap } from 'react-leaflet'
import Spinner from '../Spinner/Spinner'
import { useDispatch, useSelector } from 'react-redux'
import './MissionCAST.css'
import L, { LatLngExpression } from 'leaflet'
import { renderToString } from 'react-dom/server'
import { deselectMenuItemById } from '../../menus/SideNavigation/SideNavigationSlice'
import { ZoomablePannablePopupContent } from '../ZoomableContent/ZoomableContent'
import ReactDOM from 'react-dom'
import { RootState } from '../../store'
import useErrorStatus from '../../hooks/UseErrorStatus'
import { MenuLabels } from '../../constants/MenuLabels'

export const MissionCAST = function (): ReactElement {
  const defaultSpinnerHtml = useMemo(
    () =>
      renderToString(
        Spinner({ message: 'Your custom analytics is being generated' })
      ),
    []
  )

  const [content, setContent] = useState<string>(defaultSpinnerHtml)
  const [popup, setPopup] = useState<L.Popup | null>(null)
  const location = useSelector((state: RootState) => state.user.defaultLocation)
  const selectedItemId = useSelector(
    (state: RootState) => state.sideNav.selectedItems
  )

  const dispatch = useDispatch()
  const map = useMap()
  const errorStatus = useErrorStatus()

  const controllerRef = useRef(new AbortController())

  useEffect(() => {
    const controller = controllerRef.current
    const signal = controller.signal

    dispatch(
      fetchMissionCAST({
        lat: location.latitude,
        long: location.longitude,
        signal,
        controller,
      })
    )
      .unwrap()
      .then((response) => {
        setContent(response)
      })
      .catch((error) => {
        errorStatus.addMessage(
          'MissionCAST: ' + error.message,
          400,
          'error',
          false,
          ErrorTimeouts.MISSIONCAST
        )
        map.closePopup()
      })

    return () => {
      controller.abort()
      map.closePopup()
    }
  }, [])

  const createMissionCastRender = useCallback(() => {
    return (
      <div id="missionCast" className="bg-white p-2 overflow-visible">
        <div dangerouslySetInnerHTML={{ __html: content }} />
      </div>
    )
  }, [content])

  const makeDraggable = useCallback(
    (popup: L.Popup) => {
      const pos = map.latLngToLayerPoint(popup.getLatLng() as LatLngExpression)
      L.DomUtil.setPosition(popup._wrapper.parentNode, pos)
      const draggable = new L.Draggable(popup._container, popup._wrapper)
      draggable.enable()
    },
    [map]
  )

  useEffect(() => {
    const htmlRender = createMissionCastRender()
    const container = document.createElement('div')
    ReactDOM.render(
      <ZoomablePannablePopupContent content={htmlRender} />,
      container
    )
    popup?.setContent(container)
  }, [content])

  useEffect(() => {
    const newPopup = new L.Popup({
      minWidth: window.innerWidth * 1.1,
    })
      .setContent(content)
      .setLatLng([location.latitude, location.longitude])
      .openOn(map)
      .on('remove', () => {
        dispatch(toggleMissionCast(false))
        if (selectedItemId.includes(MenuLabels.MissionCAST)) {
          dispatch(deselectMenuItemById(MenuLabels.MISSIONCAST))
        }
      })

    setPopup(newPopup)
    makeDraggable(newPopup)

    return () => {
      newPopup.removeFrom(map)
      setPopup(null)
    }
  }, [location])

  return null
}
